import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { askApi, AskResponse } from '@/lib/ask';
import { snapshotsApi } from '@/lib/snapshots';
import { SaveResultButton } from '@/components/app/SaveResultButton';
import { ExampleChips } from '@/components/app/ExampleChips';
import { FeedbackButtons } from '@/components/app/FeedbackButtons';
import { AppShell } from '@/components/app/AppShell';
import { HelpHint } from '@/components/app/HelpHint';
import { InlineAlert } from '@/components/app/Modal';
import { CommentThread } from '@/components/CommentThread';
import { CitedMarkdown } from '@/components/ui/CitedMarkdown';
import { SavedPrompts } from '@/components/app/SavedPrompts';
import { modelTierLabel } from '@/lib/model-label';

interface RenderedTurn {
  role: 'user' | 'assistant';
  content: string;
  citedChunkIds?: string[];
  relatedQuestions?: string[];
  model?: string | null;
  costCents?: number | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
  /** Persisted conversation_messages id (set on completion / when loaded). */
  messageId?: string;
  /** True while tokens are still streaming into this assistant turn. */
  streaming?: boolean;
}

/**
 * AskPage — the primary surface. A single input. Each turn renders
 * inline citation chips ([chunk:NNN] becomes clickable). The current
 * conversation is kept locally and the server appends each turn to a
 * persisted conversation_messages row.
 */
export function AskPage() {
  const { t } = useTranslation();
  const { wsId, conversationId: routeConvId } = useParams<{ wsId: string; conversationId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const seedConsumed = useRef(false);
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState<RenderedTurn[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>(routeConvId);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const abortRef = useRef<(() => void) | null>(null);
  // True once the user has asked in this session — the in-memory turns are then
  // authoritative, so the deep-link loader must NOT re-fetch and overwrite the
  // (not-yet-persisted) streaming answer when the URL gains the new conv id.
  const startedRef = useRef(false);

  const copyDiscussionLink = async () => {
    if (!conversationId || !wsId) return;
    const url = `${window.location.origin}/app/workspace/${wsId}/ask/${conversationId}`;
    try {
      await navigator.clipboard?.writeText(url);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2400);
    } catch {
      /* clipboard blocked — no-op */
    }
  };

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Abort any in-flight stream if the page unmounts.
  useEffect(() => () => abortRef.current?.(), []);

  // Deep link: when opened at /ask/:conversationId, load that thread so a
  // teammate can read it and join the Discussion. Skip if we started a
  // conversation in this session (streaming owns the turns) — otherwise the
  // self-navigation to /ask/:id would re-fetch and wipe the live answer.
  useEffect(() => {
    if (!routeConvId || !wsId || startedRef.current) return;
    let cancelled = false;
    (async () => {
      const res = await askApi.getConversation(wsId, routeConvId);
      if (cancelled || !res?.conversation) return;
      setConversationId(res.conversation.id);
      setTurns(
        res.messages.map((m) => ({
          role: m.role,
          content: m.content,
          citedChunkIds: m.cited_chunk_ids ?? undefined,
          model: m.model,
          costCents: m.cost_cents,
          messageId: m.id,
        })),
      );
    })().catch(() => {
      /* not found / no access — leave the page empty */
    });
    return () => {
      cancelled = true;
    };
  }, [routeConvId, wsId]);

  // Keep the URL in sync once a conversation exists, so the thread is
  // shareable and survives a refresh.
  useEffect(() => {
    if (conversationId && wsId && conversationId !== routeConvId) {
      navigate(`/app/workspace/${wsId}/ask/${conversationId}`, { replace: true });
    }
  }, [conversationId, wsId, routeConvId, navigate]);

  // Seeded ask: any "Ask about this" CTA across the app links here with
  // ?seed=<context>. Prefill and auto-run the first turn once.
  useEffect(() => {
    const seed = searchParams.get('seed');
    if (seed && !seedConsumed.current && !routeConvId && turns.length === 0) {
      seedConsumed.current = true;
      void submit(seed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, routeConvId]);

  // Apply an update to the last assistant turn (the one currently streaming).
  const patchLastAssistant = (patch: (t: RenderedTurn) => RenderedTurn) =>
    setTurns((prev) => {
      const copy = [...prev];
      for (let i = copy.length - 1; i >= 0; i--) {
        if (copy[i].role === 'assistant') {
          copy[i] = patch(copy[i]);
          break;
        }
      }
      return copy;
    });

  const finalize = (resp: AskResponse) => {
    setConversationId(resp.conversationId);
    patchLastAssistant((tn) => ({
      ...tn,
      content: resp.answer,
      citedChunkIds: resp.citedChunkIds,
      relatedQuestions: resp.relatedQuestions,
      model: resp.model,
      costCents: resp.costCents,
      inputTokens: resp.inputTokens,
      outputTokens: resp.outputTokens,
      messageId: resp.messageId,
      streaming: false,
    }));
    setIsPending(false);
    abortRef.current = null;
  };

  const submit = async (qOverride?: string) => {
    const q = (qOverride ?? question).trim();
    if (!q || isPending) return;
    startedRef.current = true;
    setError(null);
    setQuestion('');
    // Push the user turn + an empty assistant turn we stream into.
    setTurns((prev) => [
      ...prev,
      { role: 'user', content: q },
      { role: 'assistant', content: '', streaming: true },
    ]);
    setIsPending(true);

    abortRef.current = askApi.askStream(
      wsId!,
      { question: q, conversationId },
      {
        onMeta: (meta) => {
          setConversationId(meta.conversationId);
          patchLastAssistant((tn) => ({ ...tn, model: meta.model }));
        },
        onToken: (text) => {
          patchLastAssistant((tn) => ({ ...tn, content: tn.content + text }));
        },
        onDone: (resp) => finalize(resp),
        onError: async (message) => {
          // Graceful fallback: retry once on the non-streaming endpoint so a
          // transient stream failure still yields an answer.
          try {
            const resp = await askApi.ask(wsId!, { question: q, conversationId });
            finalize(resp);
          } catch {
            setError(message);
            setIsPending(false);
            abortRef.current = null;
            // Drop the empty assistant placeholder.
            setTurns((prev) => {
              const copy = [...prev];
              const last = copy[copy.length - 1];
              if (last && last.role === 'assistant' && !last.content) copy.pop();
              return copy;
            });
          }
        },
      },
    );
  };

  return (
    <AppShell
      title={t('ask.title', { defaultValue: 'Ask' })}
      description={
        conversationId
          ? (t('ask.thread', { defaultValue: 'Thread #{{id}}', id: conversationId.slice(0, 8) }) as string)
          : (t('ask.new', { defaultValue: 'New thread' }) as string)
      }
      maxWidth="max-w-4xl"
    >
      <div className="flex flex-col gap-6">
        {turns.length === 0 && <HelpHint page="ask" />}
        {turns.length === 0 && (
          <>
            <div className="paper-spotlight rounded-2xl p-10 text-center">
              <h2 className="display serif-jp text-2xl font-semibold text-ink-900 leading-tight text-balance">
                {t('ask.empty.heading', { defaultValue: 'Every document. Every signal. Every thought.' })}
              </h2>
              <p className="mt-3 text-[13.5px] text-ink-600 max-w-md mx-auto">
                {t('ask.empty.subheading', { defaultValue: 'Ask anything across your indexed corpus. Every answer cites the chunks it used.' })}
              </p>
            </div>
            <ExampleChips
              page="ask"
              onPick={(text) => {
                setQuestion(text);
                inputRef.current?.focus();
              }}
            />
          </>
        )}

        {turns.map((turn, idx) => (
          <TurnView
            key={idx}
            turn={turn}
            wsId={wsId!}
            conversationId={conversationId}
            query={turn.role === 'assistant' ? turns[idx - 1]?.content : undefined}
            onAsk={(q) => void submit(q)}
          />
        ))}

        {conversationId && turns.some((t) => t.role === 'assistant') && (
          <div className="card-paper p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-[0.14em] text-ink-500 font-semibold">
                {t('ask.discussion', { defaultValue: 'Discussion' })}
              </div>
              <button
                onClick={copyDiscussionLink}
                className="text-[11px] text-accent-600 hover:underline"
                title={t('ask.copy_link_hint', { defaultValue: 'Anyone in this workspace can open this thread and join the discussion.' }) as string}
              >
                {linkCopied
                  ? t('ask.link_copied', { defaultValue: 'Link copied ✓' })
                  : t('ask.copy_link', { defaultValue: 'Copy link to discussion' })}
              </button>
            </div>
            <CommentThread
              workspaceId={wsId!}
              targetKind="conversation"
              targetId={conversationId}
            />
          </div>
        )}

        {isPending && !turns[turns.length - 1]?.content && (
          <div className="flex items-center gap-2 text-sm text-ink-500">
            <span className="inline-block w-2 h-2 bg-accent-500 rounded-full animate-pulse" />
            {t('ask.thinking', { defaultValue: 'Searching · retrieving · composing…' })}
          </div>
        )}

        {error && (
          <InlineAlert tone="error">
            {error}
          </InlineAlert>
        )}

        <div className="sticky bottom-4 mt-2">
          <div className="mb-2">
            <SavedPrompts
              wsId={wsId!}
              agentKind="ask"
              currentText={question}
              onPick={(body) => {
                setQuestion(body);
                inputRef.current?.focus();
              }}
            />
          </div>
          <div className="card-paper p-3 shadow-card">
            <textarea
              ref={inputRef}
              rows={2}
              className="w-full resize-none bg-transparent outline-none text-sm text-ink-900 placeholder:text-ink-400"
              placeholder={t('ask.placeholder', { defaultValue: 'Ask a question. Be specific. ↩ to send, ⇧↩ for a new line.' }) as string}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends; Shift+Enter (or ⌘/Ctrl+Enter) inserts a newline.
                if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
                  e.preventDefault();
                  void submit();
                }
              }}
            />
            <div className="flex items-center justify-between pt-2 text-xs text-ink-500">
              <span className="num">↩ {t('ask.to_send', { defaultValue: 'to send' })} · ⇧↩ {t('ask.newline', { defaultValue: 'new line' })}</span>
              <button
                onClick={() => submit()}
                disabled={!question.trim() || isPending}
                className="btn-press px-3.5 py-1.5 text-xs"
              >
                {t('ask.send', { defaultValue: 'Send' })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function TurnView({ turn, wsId, conversationId, query, onAsk }: { turn: RenderedTurn; wsId: string; conversationId?: string; query?: string; onAsk?: (q: string) => void }) {
  const { t } = useTranslation();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [snapPending, setSnapPending] = useState(false);
  if (turn.role === 'user') {
    return (
      <div className="self-end max-w-2xl rounded-xl bg-accent-500 text-white px-4 py-3 whitespace-pre-wrap text-sm shadow-press">
        {turn.content}
      </div>
    );
  }

  const snapshot = async () => {
    if (snapPending) return;
    setSnapPending(true);
    try {
      const snap = await snapshotsApi.create(wsId, {
        payload: {
          kind: 'ask_answer',
          answer: turn.content,
          citedChunkIds: turn.citedChunkIds ?? [],
          model: turn.model ?? null,
          costCents: turn.costCents ?? 0,
          inputTokens: turn.inputTokens ?? 0,
          outputTokens: turn.outputTokens ?? 0,
        },
        conversationId,
      });
      const url = snapshotsApi.publicUrl(snap.id);
      setShareUrl(url);
      try { await navigator.clipboard?.writeText(url); } catch { /* clipboard may be blocked — UI still shows the URL */ }
    } finally {
      setSnapPending(false);
    }
  };

  return (
    <article className="card-paper px-5 py-4">
      <CitedAnswer content={turn.content} wsId={wsId} />
      {turn.streaming && (
        <span className="inline-block w-1.5 h-4 align-middle bg-accent-500/70 animate-pulse rounded-sm" />
      )}
      {!turn.streaming && (
      <footer className="mt-3 pt-2 border-t border-ink-100 text-[11px] text-ink-500 flex items-center justify-between">
        <span className="num">
          {modelTierLabel(turn.model)} · {(turn.inputTokens ?? 0) + (turn.outputTokens ?? 0)} tok · ¢{turn.costCents ?? 0}
        </span>
        <span className="flex items-center gap-3">
          {turn.messageId && (
            <FeedbackButtons
              wsId={wsId}
              kind="ask"
              targetId={turn.messageId}
              query={query}
              citedChunkIds={turn.citedChunkIds}
            />
          )}
          <span>
            {turn.citedChunkIds?.length
              ? `${turn.citedChunkIds.length} ${t('ask.citations', { defaultValue: 'citations' })}`
              : t('ask.no_citations', { defaultValue: 'no citations' })}
          </span>
          <button
            onClick={snapshot}
            disabled={snapPending}
            className="text-accent-600 hover:underline disabled:opacity-50"
          >
            {snapPending
              ? t('ask.snapshotting', { defaultValue: 'Sharing…' })
              : t('ask.snapshot', { defaultValue: 'Share snapshot' })}
          </button>
          <SaveResultButton
            workspaceId={wsId}
            kind="search"
            title={turn.content.replace(/\s+/g, ' ').slice(0, 80)}
            output={turn.content}
            citedChunkIds={turn.citedChunkIds}
            model={turn.model ?? undefined}
            costCents={turn.costCents ?? undefined}
          />
        </span>
      </footer>
      )}
      {!turn.streaming && turn.relatedQuestions && turn.relatedQuestions.length > 0 && onAsk && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-[0.14em] text-ink-400 font-semibold mb-1.5">
            {t('ask.related', { defaultValue: 'Ask next' })}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {turn.relatedQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => onAsk(q)}
                className="text-left text-[12.5px] rounded-full border border-accent-200 bg-accent-50/60 text-accent-700 px-3 py-1 hover:bg-accent-50 transition"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}
      {shareUrl && (
        <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5 flex items-center justify-between gap-2">
          <span className="num truncate">{shareUrl}</span>
          <span className="text-[10px] uppercase tracking-[0.12em] font-semibold">
            {t('ask.snapshot_copied', { defaultValue: 'copied to clipboard' })}
          </span>
        </div>
      )}
    </article>
  );
}

function CitedAnswer({ content, wsId }: { content: string; wsId: string }) {
  // Shared renderer: styled markdown + inline [chunk:NNN] citation pills.
  return <CitedMarkdown content={content} wsId={wsId} />;
}

